import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Newspaper, Loader2, MapPin } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import CtaBand from "./CtaBand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export const BlogPage = () => {
  useSeo({
    title: "Blog — Tips over dak- en gevelreiniging | SoftPro",
    description:
      "Praktische tips en lokaal advies over softwash, dak-, gevel-, terras- en bedrijfspandreiniging van de specialisten van SoftPro.",
  });

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then(({ data }) => setArticles(data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      {/* Header */}
      <section className="relative overflow-hidden bg-navy-deep grain pt-36 pb-20 sm:pt-44 sm:pb-24">
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-teal ring-1 ring-white/15">
            <Newspaper className="h-4 w-4" /> SoftPro Blog
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl sm:text-6xl font-bold tracking-tight text-white leading-[1.02]">
            Tips & kennis over buitenreiniging
          </h1>
          <p className="mt-6 max-w-xl text-lg text-white/70 leading-relaxed">
            Praktisch advies over softwash, dak-, gevel-, terras- en
            bedrijfspandreiniging — plus lokale tips voor jouw regio.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-background py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-navy" />
            </div>
          ) : articles.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-card py-20 text-center text-muted-foreground">
              Binnenkort verschijnen hier onze eerste artikelen.
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {articles.map((a, i) => (
                <motion.div
                  key={a.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                >
                  <Link
                    to={`/blog/${a.slug}`}
                    data-testid={`blog-card-${a.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={a.cover_image}
                        alt={a.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-navy backdrop-blur">
                        {a.category === "Lokale SEO" && <MapPin className="h-3 w-3" />}
                        {a.category}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <p className="text-xs text-muted-foreground">{fmtDate(a.published_at)}</p>
                      <h2 className="mt-2 font-display text-xl font-bold leading-snug text-ink group-hover:text-navy">
                        {a.title}
                      </h2>
                      <p className="mt-3 flex-1 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                        {a.excerpt}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-teal">
                        Lees verder
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <CtaBand />
    </div>
  );
};

export default BlogPage;
